import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import client from '../../../lib/feathers';

const DEFAULT_OPT_IN_COPY = 'By participating in this passport, you consent to share your contact information (name, email, check-in date) with the venues you visit during the passport period.';

export default function PassportNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    completionThreshold: '5',
    status: 'draft',
    optInCopy: '',
    askMembership: false,
  });
  const [thresholdError, setThresholdError] = useState('');

  // Validate completion threshold format
  const validateThreshold = (value) => {
    const cleaned = value.replace(/\s/g, '');
    if (!cleaned) {
      setThresholdError('Completion threshold is required');
      return false;
    }
    if (!/^(\d+)(,\d+)*$/.test(cleaned)) {
      setThresholdError('Enter numbers separated by commas (e.g., "3" or "3,5,7")');
      return false;
    }
    const numbers = cleaned.split(',').map(n => parseInt(n, 10));
    if (numbers.some(n => n < 1)) {
      setThresholdError('All values must be at least 1');
      return false;
    }
    setThresholdError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateThreshold(formData.completionThreshold)) {
      return;
    }
    try {
      setSaving(true);
      const service = client.service('admin/passport');
      const result = await service.create(formData);
      // Navigate to edit page so they can upload cover image and add listings
      navigate(`/admin/passports/${result.id}/edit`);
    } catch (err) {
      console.error('Error creating passport:', err);
      alert(err.message || 'Failed to create passport');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title="Create Passport">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Passports', href: '/admin/passports' },
          { name: 'New Passport' }
        ]} />
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        {/* Basic Info */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title *
              </label>
              <input
                type="text"
                id="title"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Supports Markdown formatting</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                  Start Date *
                </label>
                <input
                  type="date"
                  id="startDate"
                  required
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                  End Date *
                </label>
                <input
                  type="date"
                  id="endDate"
                  required
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="completionThreshold" className="block text-sm font-medium text-gray-700">
                  Completion Milestones *
                </label>
                <input
                  type="text"
                  id="completionThreshold"
                  required
                  value={formData.completionThreshold}
                  onChange={(e) => {
                    setFormData({ ...formData, completionThreshold: e.target.value });
                    validateThreshold(e.target.value);
                  }}
                  placeholder="e.g., 3 or 3,5,7"
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm ${thresholdError ? 'border-red-300' : ''}`}
                />
                {thresholdError ? (
                  <p className="mt-1 text-xs text-red-600">{thresholdError}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Enter one number or multiple (e.g., &quot;3,5,7&quot;). First number = completion, others are bonus milestones.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="ended">Ended</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Opt-In Settings */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Opt-In Settings</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="optInCopy" className="block text-sm font-medium text-gray-700">
                Opt-In Message
              </label>
              <textarea
                id="optInCopy"
                rows={3}
                value={formData.optInCopy}
                onChange={(e) => setFormData({ ...formData, optInCopy: e.target.value })}
                placeholder={DEFAULT_OPT_IN_COPY}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave blank to use the default opt-in message
              </p>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="askMembership"
                  type="checkbox"
                  checked={formData.askMembership}
                  onChange={(e) => setFormData({ ...formData, askMembership: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="askMembership" className="font-medium text-gray-700">
                  Ask about club memberships
                </label>
                <p className="text-gray-500">
                  When enabled, users will be asked which wine clubs they belong to when opting in
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Info about cover image */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <p className="text-sm text-blue-700">
            After creating the passport, you&apos;ll be able to add a cover image, manage listings, and configure additional settings on the edit page.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/passports')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Passport'}
          </button>
        </div>
      </form>
    </Page>
  );
}
